import { AccountInfo } from "@azure/msal-browser";
import { loginRequest } from "../msal/msalConfig";
import { msalInstance } from "../msal/msalInstance";
import {
  SAFETY_BUFFER_SECONDS,
  SUPERSET_STORAGE_KEYS,
} from "@/shared/constants";

let isSessionRecoveryInProgress = false;

export const getToken = (): Promise<string> => {
  const account = msalInstance.getActiveAccount();

  const getAccessToken = async (): Promise<string> => {
    if (!account) {
      await msalInstance.loginRedirect(loginRequest);
      throw new Error("No account found. Redirecting to login.");
    }
    const exp = account.idTokenClaims?.exp;

    // If no expiry claim → force refresh
    if (!exp) {
      return refreshToken(account);
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);

    // Refresh if expired OR within safety buffer
    const isExpiringSoon = exp - nowInSeconds <= SAFETY_BUFFER_SECONDS;

    if (isExpiringSoon) {
      return refreshToken(account);
    }

    return account?.idToken || ""; // Return the token or an empty string if not available
  };

  return getAccessToken();
};

export const getSupersetAuthToken = async (
  useRefreshToken: boolean,
): Promise<string | null> => {
  const token = useRefreshToken
    ? localStorage.getItem(SUPERSET_STORAGE_KEYS.refreshToken)
    : localStorage.getItem(SUPERSET_STORAGE_KEYS.authToken);
  return token;
};

export const refreshToken = async (
  refAccount: AccountInfo,
): Promise<string> => {
  try {
    const response = await msalInstance.acquireTokenSilent({
      account: refAccount,
      scopes: loginRequest.scopes,
      forceRefresh: true,
    });

    return response.idToken;
  } catch {
    return forceLogoutAndRedirectToLogin();
  }
};

export const forceLogoutAndRedirectToLogin = async (): Promise<never> => {
  if (isSessionRecoveryInProgress) {
    throw new Error("Session recovery is already in progress.");
  }

  isSessionRecoveryInProgress = true;

  const activeAccount = msalInstance.getActiveAccount();

  try {
    if (activeAccount) {
      Object.keys(localStorage).forEach((key) => {
        if (!key.toLowerCase().startsWith("msal")) {
          localStorage.removeItem(key);
        }
      });
      await msalInstance.logoutRedirect({
        account: activeAccount,
        postLogoutRedirectUri: window.location.origin,
      });
    } else {
      await msalInstance.loginRedirect(loginRequest);
    }
  } catch {
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch {
      isSessionRecoveryInProgress = false;
      throw new Error("Session recovery failed. Unable to redirect to login.");
    }
  }

  throw new Error("Session expired. Redirecting to login.");
};
